import React, { useMemo, useState } from "react";
import { Collapse } from "antd";
import { BulbOutlined, RightOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import styles from "./InfoPanel.module.scss";

// Recuerda si el usuario cerro la guia (onboarding: abierta hasta el 1er cierre)
const STORAGE_KEY = "educa.quickGuide.collapsed";

function readInitialOpen(fallback) {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (v === null) return true; // primera visita: abierta
    return v !== "1"; // "1" = el usuario la cerro
  } catch {
    return fallback;
  }
}

/**
 * Tarjeta de un paso: zona superior con tinte + ilustracion grande (opcional)
 * y badge de numero; zona inferior con titulo + descripcion.
 * Si no hay `illustration`, la zona muestra solo el numero (degrada con gracia).
 */
function StepCard({ index, title, description, illustration }) {
  return (
    <div className={styles.stepCard}>
      <div className={styles.illuZone}>
        <div className={styles.badge}>{index}</div>
        {illustration ? (
          <img
            src={illustration}
            alt=""
            aria-hidden="true"
            className={styles.illu}
            loading="lazy"
          />
        ) : null}
      </div>
      <div className={styles.textZone}>
        <div className={styles.stepTitle}>{title}</div>
        {description ? (
          <div className={styles.stepDesc}>{description}</div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Cuerpo de la guia. Desktop: timeline horizontal de cards con flechas.
 * Movil: stepper compacto (numeros) + solo el paso activo desplegado.
 */
function GuideBody({ items }) {
  const [active, setActive] = useState(0);
  const activeItem = items[active] || items[0];

  return (
    <>
      {/* Desktop: timeline */}
      <div className={styles.timeline} role="list">
        {items.map((it, idx) => (
          <React.Fragment key={`${it.title}-${idx}`}>
            <StepCard
              index={idx + 1}
              title={it.title}
              description={it.description}
              illustration={it.illustration}
            />
            {idx < items.length - 1 ? (
              <span className={styles.connector} aria-hidden="true">
                &#8594;
              </span>
            ) : null}
          </React.Fragment>
        ))}
      </div>

      {/* Movil: stepper + paso activo */}
      <div className={styles.mobileGuide}>
        <div className={styles.stepper} role="tablist">
          {items.map((it, idx) => (
            <React.Fragment key={`dot-${idx}`}>
              <button
                type="button"
                role="tab"
                aria-selected={idx === active}
                aria-label={it.title}
                className={`${styles.stepDot} ${
                  idx === active ? styles.stepDotActive : ""
                }`}
                onClick={() => setActive(idx)}
              >
                {idx + 1}
              </button>
              {idx < items.length - 1 ? (
                <span className={styles.stepLine} aria-hidden="true" />
              ) : null}
            </React.Fragment>
          ))}
        </div>

        {activeItem ? (
          <StepCard
            index={active + 1}
            title={activeItem.title}
            description={activeItem.description}
            illustration={activeItem.illustration}
          />
        ) : null}
      </div>
    </>
  );
}

/**
 * Guia rapida colapsable, con cabecera tipo CTA (tinte + icono + "Ver guia").
 * Se abre sola la primera visita y recuerda si el usuario la cierra.
 * @param {{title:string, description?:string, illustration?:string}[]} items
 */
export function InfoPanel({ title, items = [], initiallyOpen = false }) {
  const { t } = useTranslation("common");
  const resolvedTitle = title ?? t("quickGuide");
  const [open, setOpen] = useState(() => readInitialOpen(initiallyOpen));

  const handleChange = (keys) => {
    const isOpen = Array.isArray(keys) ? keys.length > 0 : Boolean(keys);
    setOpen(isOpen);
    try {
      window.localStorage.setItem(STORAGE_KEY, isOpen ? "0" : "1");
    } catch {
      /* ignore */
    }
  };

  const collapseItems = useMemo(
    () => [
      {
        key: "guide",
        label: (
          <span className={styles.ctaLabel}>
            <BulbOutlined className={styles.ctaIcon} />
            {resolvedTitle}
          </span>
        ),
        children: <GuideBody items={items} />,
      },
    ],
    [items, resolvedTitle],
  );

  return (
    <Collapse
      className={styles.collapse}
      items={collapseItems}
      activeKey={open ? ["guide"] : []}
      onChange={handleChange}
      expandIconPlacement="end"
      expandIcon={({ isActive }) => (
        <span className={styles.ctaAction}>
          {isActive ? t("hideGuide") : t("viewGuide")}
          <RightOutlined
            className={styles.ctaChevron}
            rotate={isActive ? 90 : 0}
          />
        </span>
      )}
    />
  );
}
